import { type FormEvent, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLES } from '@lms/shared-types';
import { Button, Modal, useToast } from '@lms/ui';
import { SearchEmptyState } from '@/components/SearchEmptyState';
import { SearchInput } from '@/components/SearchInput';
import { api, listAllResource, listResource } from '@/lib/api';
import { matchesSearch } from '@/lib/search-normalize';
import { enrollmentSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';

interface EnrollableUser {
  id: string;
  name: string;
  fullName?: string;
  email: string;
  status?: string;
  roles?: string[];
}

interface EnrollmentRow {
  user?: { id: string };
}

interface EnrollStudentModalProps {
  open: boolean;
  courseId: string;
  courseName: string;
  onClose: () => void;
}

export function EnrollStudentModal({ open, courseId, courseName, onClose }: EnrollStudentModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [query, setQuery] = useState('');
  const [userId, setUserId] = useState('');

  const { data: enrolledData } = useQuery({
    queryKey: ['enrollments', courseId, 'enrolled-ids'],
    queryFn: () => listResource<EnrollmentRow>('/enrollments', { courseId, perPage: 500 }),
    enabled: open,
  });

  const enrolledUserIds = useMemo(() => {
    const ids = new Set<string>();
    for (const row of enrolledData?.data ?? []) {
      if (row.user?.id) ids.add(row.user.id);
    }
    return ids;
  }, [enrolledData?.data]);

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', 'enrollable'],
    queryFn: () => listAllResource<EnrollableUser>('/users'),
    enabled: open,
  });

  const availableUsers = useMemo(
    () =>
      (usersData?.data ?? []).filter(
        (user) =>
          user.status === 'active' &&
          (user.roles ?? []).includes(ROLES.STUDENT) &&
          !enrolledUserIds.has(user.id),
      ),
    [usersData?.data, enrolledUserIds],
  );

  const enrollableUsers = useMemo(() => {
    const term = query.trim();
    if (!term) return availableUsers;
    return availableUsers.filter((user) => matchesSearch(`${user.fullName ?? user.name} ${user.email}`, term));
  }, [availableUsers, query]);

  const enrollMutation = useMutation({
    mutationFn: (selectedUserId: string) =>
      api('/enrollments/assign', {
        method: 'POST',
        body: { userId: selectedUserId, courseId },
      }),
    onSuccess: async () => {
      toast(enrollmentSuccessToast('create'));
      await queryClient.invalidateQueries({ queryKey: ['enrollments'] });
      setUserId('');
      setQuery('');
      onClose();
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo crear la matrícula'));
    },
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!userId) return;
    enrollMutation.mutate(userId);
  }

  function handleClose() {
    if (enrollMutation.isPending) return;
    setUserId('');
    setQuery('');
    onClose();
  }

  return (
    <Modal open={open} onClose={handleClose} title="Matricular estudiante" size="lg" footer={null}>
      <div className="space-y-4">
        <p className="text-sm text-[#6c757d]">Selecciona un alumno para inscribirlo en {courseName}.</p>

        <label className="mb-2 block text-sm font-medium text-surface-700" htmlFor="enroll-user-search">
          Alumno
        </label>
        <SearchInput
          id="enroll-user-search"
          value={query}
          onChange={setQuery}
          placeholder="Buscar por nombre o email..."
          aria-label="Buscar por nombre o email"
        />
      <form onSubmit={handleSubmit} className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-[#6c757d]">Cargando alumnos…</p>
        ) : availableUsers.length === 0 ? (
          <p className="text-sm text-[#6c757d]">No hay alumnos disponibles para matricular.</p>
        ) : enrollableUsers.length === 0 ? (
          <SearchEmptyState term={query.trim()} onClear={() => setQuery('')} />
        ) : (
          <div>
            <div className="max-h-64 overflow-y-auto rounded-lg border border-[#dee2e6]">
              {enrollableUsers.map((user) => {
                const displayName = user.fullName ?? user.name;
                return (
                  <label
                    key={user.id}
                    className="flex cursor-pointer items-center gap-3 border-b border-[#dee2e6] px-3 py-2 last:border-b-0 hover:bg-[#f8f9fa]"
                  >
                    <input
                      type="radio"
                      name="user_id"
                      value={user.id}
                      checked={userId === user.id}
                      onChange={() => setUserId(user.id)}
                      className="border-[#ced4da] text-brand-600 focus:ring-brand-500"
                      required={enrollableUsers.length > 0}
                    />
                    <span className="min-w-0 text-sm">
                      <span className="font-medium text-[#212529]">{displayName}</span>
                      <span className="text-[#6c757d]"> — {user.email}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 border-t border-outline/40 pt-4">
          <Button type="button" variant="outline" onClick={handleClose} disabled={enrollMutation.isPending}>
            Cancelar
          </Button>
          {enrollableUsers.length > 0 && (
            <Button type="submit" loading={enrollMutation.isPending}>
              Matricular
            </Button>
          )}
        </div>
      </form>
      </div>
    </Modal>
  );
}
